import { FormControl, InputLabel, MenuItem, Select, Tooltip, IconButton } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useEffect, useRef } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface FormikSelectProps {
  disabled?: boolean;
  label: any;
  fieldValue?: {
    value: string | number;
    onBlur?: () => void;
  };
  options: (string | number)[];
  descriptions?: string[];
  handleChange: (event: SelectChangeEvent<string | number>) => void;
  isErrorField?: boolean;
  name: string;
  initialValue?: string | number;
  type?: string;
  helpIcon?: boolean;
  helpContent?: string;
  required?: boolean;
}

export default function FormikSelect({
  disabled = false,
  label,
  fieldValue,
  options,
  descriptions,
  handleChange,
  isErrorField = false,
  name,
  initialValue,
  helpIcon = false,
  helpContent = '',
  required = false,
}: FormikSelectProps) {
  const handleChangeRef = useRef(handleChange);
  handleChangeRef.current = handleChange;

  useEffect(() => {
    const hasValue =
      fieldValue?.value !== undefined &&
      fieldValue?.value !== null &&
      fieldValue?.value !== '';
    if (!hasValue && initialValue !== undefined && initialValue !== null && initialValue !== '') {
      handleChangeRef.current({
        target: { name, value: initialValue }
      } as SelectChangeEvent<string | number>);
    }
  }, [fieldValue?.value, initialValue, name]);

  const renderMenuItem = (item: string | number, index: number) => {
    const description = descriptions && descriptions[index] ? descriptions[index] : undefined;
    
    return (
      <MenuItem 
        key={`${item}+${index}`} 
        id={`${item}+${index}`} 
        value={item} 
        title={description} // Native browser tooltip - this definitely works!
        sx={{ 
          textWrap: "auto",
          cursor: description ? 'help' : 'pointer' // Visual indicator when description exists
        }}
      >
        {item}
      </MenuItem>
    );
  };

  return (
    <FormControl
      fullWidth
      className="select-drop-down-label-styling required-field"
      sx={{ position: 'relative' }}
    >
      <InputLabel
        id="demo-simple-select-helper-label"
        className="input-field-dropdown-styling required-label weight-600"
      >
        {label}
      </InputLabel>
      <Select
        error={required && !fieldValue?.value && isErrorField}
        disabled={disabled}
        label={label}
        value={fieldValue?.value ?? ''}
        onBlur={fieldValue.onBlur}
        onChange={handleChange}
        labelId="demo-simple-select-helper-label"
        id={name}
        name={name}
        className={'input-field-styling'}
        MenuProps={{ 
          PaperProps: { 
            style: { 
              maxHeight: 200 
            } 
          } 
        }}
        sx={{
          paddingRight: helpIcon && helpContent ? '48px !important' : 'inherit',
          '& .MuiSelect-select': {
            paddingRight: helpIcon && helpContent ? '48px !important' : 'inherit',
          }
        }}
      >
        {options?.length > 0 &&
          options.map((item, index) => renderMenuItem(item, index))}
      </Select>
      
      {helpIcon && helpContent && (
        <Tooltip 
          title={helpContent} 
          arrow 
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '14px',
                maxWidth: '500px',
                whiteSpace: 'pre-wrap'
              }
            }
          }}
        >
          <IconButton 
            size="small" 
            sx={{ 
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '4px',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <HelpOutlineIcon 
              sx={{ 
                fontSize: '18px', 
                color: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  color: 'rgba(0, 0, 0, 0.8)'
                }
              }} 
            />
          </IconButton>
        </Tooltip>
      )}
    </FormControl>
  );
}